/**
 * Analytics Event Tracker
 *
 * Tracks events to analytics_events table with allowlist validation.
 * Attaches session_id, UTMs, referrer, and device info automatically.
 */

import { supabase } from '@/integrations/supabase/client';
import {
  getSessionId,
  getCachedUserId,
  hasUserIdBeenFetched,
  setCachedUserId,
  updateSessionActivity,
} from './sessionManager';
import {
  trackGAAddToCart,
  trackGABeginCheckout,
  trackGAPurchase,
  trackGAViewContent,
} from '@/components/GoogleAnalytics';

// Canonical event allowlist
export const ALLOWED_EVENTS = [
  'page_view',
  'view_template',
  'add_to_cart',
  'remove_from_cart',
  'view_cart',
  'begin_checkout',
  'purchase',
  'download',
  'apply_promo',
  'affiliate_click',
] as const;

export type AllowedEventName = (typeof ALLOWED_EVENTS)[number];

interface EventProperties {
  [key: string]: unknown;
}

interface TrackEventOptions {
  eventName: AllowedEventName;
  properties?: EventProperties;
  pagePath?: string;
  pageTitle?: string;
}

// Cached UTM parameters (persist for session)
let cachedUtmParams: Record<string, string | null> | null = null;

// Detect device type
const getDeviceType = (): string => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = (): string => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Edg')) return 'Edge Chromium';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Get and cache UTM parameters
const getUtmParams = (): Record<string, string | null> => {
  if (cachedUtmParams) {
    return cachedUtmParams;
  }

  if (typeof window === 'undefined') {
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
    };
  }

  const params = new URLSearchParams(window.location.search);
  cachedUtmParams = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };

  // Also store UTMs in sessionStorage if present
  if (cachedUtmParams.utm_source) {
    try {
      sessionStorage.setItem('mcs_utm_params', JSON.stringify(cachedUtmParams));
    } catch (e) {
      // Ignore storage errors
    }
  } else {
    // Try to recover UTMs from sessionStorage
    try {
      const stored = sessionStorage.getItem('mcs_utm_params');
      if (stored) {
        cachedUtmParams = JSON.parse(stored);
      }
    } catch (e) {
      // Ignore storage errors
    }
  }

  return cachedUtmParams;
};

// Get user ID with caching to avoid repeated auth calls
const getUserId = async (): Promise<string | null> => {
  // Return cached value if available
  if (hasUserIdBeenFetched()) {
    return getCachedUserId();
  }

  try {
    // Use getSession instead of getUser to avoid unnecessary API call
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const userId = session?.user?.id ?? null;
    setCachedUserId(userId);
    return userId;
  } catch (error) {
    console.debug('Analytics: Error getting user ID', error);
    setCachedUserId(null);
    return null;
  }
};

/**
 * Track an analytics event
 */
export const trackEvent = async (options: TrackEventOptions): Promise<void> => {
  const { eventName, properties = {}, pagePath, pageTitle } = options;

  // Validate event name against allowlist
  if (!ALLOWED_EVENTS.includes(eventName)) {
    console.warn(`Analytics: Event "${eventName}" is not in the allowlist. Skipping.`);
    return;
  }

  // Update session activity
  updateSessionActivity();

  try {
    const sessionId = getSessionId();
    const userId = await getUserId();
    const utmParams = getUtmParams();

    const eventData = {
      event_name: eventName,
      session_id: sessionId,
      user_id: userId,
      page_path: pagePath ?? (typeof window !== 'undefined' ? window.location.pathname : null),
      page_title: pageTitle ?? (typeof document !== 'undefined' ? document.title : null),
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
      utm_term: utmParams.utm_term,
      device_type: getDeviceType(),
      browser: getBrowser(),
      screen_width: typeof window !== 'undefined' ? window.innerWidth : null,
      event_properties: properties,
    };

    // Use requestIdleCallback if available for non-blocking tracking
    const insertEvent = async () => {
      try {
        // Use page_analytics table (analytics_events is legacy/renamed)
        await supabase.from('page_analytics').insert({
          page_path: eventData.page_path ?? '/',
          page_title: eventData.page_title,
          event_type: eventData.event_name,
          session_id: eventData.session_id,
          user_id: eventData.user_id,
          referrer: eventData.referrer,
          utm_source: eventData.utm_source,
          utm_medium: eventData.utm_medium,
          utm_campaign: eventData.utm_campaign,
          utm_content: eventData.utm_content,
          utm_term: eventData.utm_term,
          device_type: eventData.device_type,
          browser: eventData.browser,
          screen_width: eventData.screen_width,
        });
      } catch (error) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics: Failed to track event', eventName, error);
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(
        insertEvent
      );
    } else {
      // Fallback: execute after a small delay
      setTimeout(insertEvent, 0);
    }
  } catch (error) {
    console.debug('Analytics: Error preparing event', error);
  }
};

/**
 * Track a page view event
 */
export const trackPageView = (pagePath?: string, pageTitle?: string): void => {
  trackEvent({
    eventName: 'page_view',
    pagePath,
    pageTitle,
  });
};

/**
 * Track template view
 */
export const trackViewTemplate = (
  templateId: string,
  templateName: string,
  categoryId?: string,
  price?: number
): void => {
  trackEvent({
    eventName: 'view_template',
    properties: {
      template_id: templateId,
      template_name: templateName,
      category_id: categoryId,
    },
  });
  // Also fire GA4 view_item event if price is provided
  if (price !== undefined) {
    trackGAViewContent(templateId, templateName, price);
  }
};

/**
 * Track add to cart
 */
export const trackAddToCart = (
  productId: string,
  productName: string,
  price: number,
  isBundle?: boolean
): void => {
  trackEvent({
    eventName: 'add_to_cart',
    properties: {
      product_id: productId,
      product_name: productName,
      price,
      is_bundle: isBundle ?? false,
    },
  });
  // Also fire GA4 add_to_cart event
  trackGAAddToCart(productId, productName, price);
};

/**
 * Track remove from cart
 */
export const trackRemoveFromCart = (productId: string, productName: string): void => {
  trackEvent({
    eventName: 'remove_from_cart',
    properties: {
      product_id: productId,
      product_name: productName,
    },
  });
};

/**
 * Track view cart
 */
export const trackViewCart = (itemCount: number, cartTotal: number): void => {
  trackEvent({
    eventName: 'view_cart',
    properties: {
      item_count: itemCount,
      cart_total: cartTotal,
    },
  });
};

/**
 * Track begin checkout
 */
export const trackBeginCheckout = (
  itemCount: number,
  cartTotal: number,
  productIds: string[],
  items?: Array<{ id: string; name: string; price: number; quantity: number }>
): void => {
  trackEvent({
    eventName: 'begin_checkout',
    properties: {
      item_count: itemCount,
      cart_total: cartTotal,
      product_ids: productIds,
    },
  });
  // Also fire GA4 begin_checkout event
  if (items && items.length > 0) {
    trackGABeginCheckout(cartTotal, 'USD', items);
  }
};

/**
 * Track purchase
 */
export const trackPurchase = (
  orderId: string,
  total: number,
  productNames: string[],
  productIds: string[],
  promoCode?: string,
  items?: Array<{ id: string; name: string; price: number; quantity: number }>
): void => {
  trackEvent({
    eventName: 'purchase',
    properties: {
      order_id: orderId,
      total,
      product_names: productNames,
      product_ids: productIds,
      promo_code: promoCode,
    },
  });
  // Also fire GA4 purchase event
  if (items && items.length > 0) {
    trackGAPurchase(orderId, total, 'USD', items);
  } else {
    // Fallback: create items from productIds and productNames
    const fallbackItems = productIds.map((id, idx) => ({
      id,
      name: productNames[idx] || 'Unknown Product',
      price: total / productIds.length,
      quantity: 1,
    }));
    trackGAPurchase(orderId, total, 'USD', fallbackItems);
  }
};

/**
 * Track download
 */
export const trackDownload = (productId: string, productName: string, fileName: string): void => {
  trackEvent({
    eventName: 'download',
    properties: {
      product_id: productId,
      product_name: productName,
      file_name: fileName,
    },
  });
};

/**
 * Track promo code applied
 */
export const trackApplyPromo = (
  promoCode: string,
  discountType: string,
  discountValue: number,
  discountAmount: number
): void => {
  trackEvent({
    eventName: 'apply_promo',
    properties: {
      promo_code: promoCode,
      discount_type: discountType,
      discount_value: discountValue,
      discount_amount: discountAmount,
    },
  });
};

/**
 * Track affiliate link click
 */
export const trackAffiliateClick = (
  affiliateCode: string,
  affiliateId?: string,
  landingPage?: string
): void => {
  trackEvent({
    eventName: 'affiliate_click',
    properties: {
      affiliate_code: affiliateCode,
      affiliate_id: affiliateId,
      landing_page: landingPage,
    },
  });
};

/**
 * Check if consent is given (respects cookie consent)
 */
export const hasAnalyticsConsent = (): boolean => {
  if (typeof localStorage === 'undefined') return true;

  // Check if cookie consent banner was acknowledged
  const consent = localStorage.getItem('mycreditscrub_cookie_consent');

  // Default to allow if no explicit consent mechanism or consent given
  // TODO: Implement granular consent if needed
  return consent !== 'denied';
};

// Export utilities for external use
export { getSessionId, getDeviceType, getBrowser, getUtmParams };

export default {
  trackEvent,
  trackPageView,
  trackViewTemplate,
  trackAddToCart,
  trackRemoveFromCart,
  trackViewCart,
  trackBeginCheckout,
  trackPurchase,
  trackDownload,
  trackApplyPromo,
  trackAffiliateClick,
  hasAnalyticsConsent,
  getSessionId,
  ALLOWED_EVENTS,
};
